import { Box } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { UploadHints } from '../../../../components/upload-hints';
import { useIsMobile } from '../../../../hooks/use-is-mobile';
import { useNotification } from '../../../../hooks/use-notification';
import { usePhotoManager } from '../../hooks/use-photo-manager';
import { getFilesKey } from '../../utils/photo-processor';
import { EmptyState } from './empty-state';
import { HiddenFileInput } from './hidden-file-input';
import { LoadingIndicator } from './loading-indicator';
import { PhotoGrid } from './photo-grid';
import { PhotoUploadHeader } from './photo-upload-header';
import type { PhotoUploadProps } from './types';

export interface PhotoUploadHandle {
  syncBackendPhotos: (
    photos: { id: string; sourceKey?: string; originalFilename: string }[],
  ) => void;
  openFileDialog: () => void;
  openAiFileDialog: () => void;
}

export const PhotoUpload = forwardRef<PhotoUploadHandle, PhotoUploadProps>(function PhotoUpload(
  {
    value,
    onChange,
    maxFiles = 10,
    label = 'Foto',
    helper,
    required = false,
    acceptPdf = false,
    onProcessingChange,
    enableAiUpload = false,
    onAiExtractRequested,
    aiButtonDisabled = false,
    hideHeader = false,
    hideHints = false,
    hideEmptyState = false,
    onPreviewReady,
  },
  ref,
) {
  const isMobile = useIsMobile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const aiFileInputRef = useRef<HTMLInputElement>(null);
  const { showError } = useNotification();
  const {
    photoItems,
    isProcessing,
    readyCount,
    addPhotos,
    removePhoto,
    syncFromExternal,
    getReadyFiles,
    syncPhotoSourceKeys,
  } = usePhotoManager({
    maxFiles,
    onError: showError,
    onPreviewReady,
  });

  useImperativeHandle(ref, () => ({
    syncBackendPhotos: syncPhotoSourceKeys,
    openFileDialog: () => fileInputRef.current?.click(),
    openAiFileDialog: () => aiFileInputRef.current?.click(),
  }));

  // Ref per tracciare la chiave corrente e evitare loop
  const lastNotifiedKeyRef = useRef('');
  const lastExternalKeyRef = useRef('');

  // Sincronizza i file esterni (es. da localStorage) nelle preview
  const valueKey = getFilesKey(value ?? []);

  useEffect(() => {
    // Ignora se è la stessa chiave che abbiamo già sincronizzato
    if (valueKey === lastExternalKeyRef.current) {
      return;
    }
    // Ignora se è la stessa chiave che abbiamo notificato noi
    if (valueKey === lastNotifiedKeyRef.current) {
      return;
    }

    lastExternalKeyRef.current = valueKey;
    syncFromExternal(value ?? []);
  }, [valueKey, syncFromExternal, value]);

  // Notifica il parent quando cambiano i file interni
  const readyFiles = getReadyFiles();
  const readyFilesKey = getFilesKey(readyFiles);

  useEffect(() => {
    // Ignora se è la stessa chiave che abbiamo già notificato
    if (readyFilesKey === lastNotifiedKeyRef.current) {
      return;
    }
    // Ignora se corrisponde all'ultimo sync esterno (evita loop)
    if (readyFilesKey === lastExternalKeyRef.current) {
      lastNotifiedKeyRef.current = readyFilesKey;
      return;
    }

    lastNotifiedKeyRef.current = readyFilesKey;
    onChange(readyFiles);
  }, [readyFilesKey, onChange, readyFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notifica il parent quando cambia lo stato di elaborazione
  useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleAiUploadClick = () => {
    aiFileInputRef.current?.click();
  };

  const handleFilesSelected = async (files: File[]) => {
    await addPhotos(files);
  };

  const handleAiFilesSelected = async (files: File[]) => {
    await addPhotos(files);
    const lastFile = files[files.length - 1];
    if (lastFile && onAiExtractRequested) {
      onAiExtractRequested(lastFile);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!hideHeader && (
        <PhotoUploadHeader
          label={label}
          helper={helper}
          required={required}
          showAddButton={readyCount < maxFiles}
          isProcessing={isProcessing}
          onAddClick={handleUploadClick}
          showAiButton={enableAiUpload}
          onAddWithAiClick={enableAiUpload ? handleAiUploadClick : undefined}
          aiButtonDisabled={aiButtonDisabled}
        />
      )}

      {!hideHints && (
        <UploadHints
          formats={acceptPdf ? 'JPG, PNG, WEBP, PDF' : 'JPG, PNG, WEBP'}
          showAiDisclaimer={enableAiUpload}
        />
      )}

      <HiddenFileInput
        ref={fileInputRef}
        onFilesSelected={handleFilesSelected}
        acceptPdf={acceptPdf}
      />

      {enableAiUpload && (
        <HiddenFileInput
          ref={aiFileInputRef}
          onFilesSelected={handleAiFilesSelected}
          acceptPdf={acceptPdf}
        />
      )}

      {photoItems.length === 0 && !isProcessing && !hideEmptyState && (
        <EmptyState isMobile={isMobile} onUploadClick={handleUploadClick} />
      )}

      {isProcessing && <LoadingIndicator />}

      {photoItems.length > 0 && <PhotoGrid items={photoItems} onRemove={removePhoto} />}
    </Box>
  );
});
